import { useCallback, useReducer } from 'react'

export type FormAction<T> =
    | { type: 'SET_FIELD'; key: keyof T; value: T[keyof T] }
    | { type: 'RESET'; payload: T };

export const formReducer = <T>(state: T, action: FormAction<T>): T => {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.key]: action.value }
        case 'RESET':
            return action.payload
        default:
            return state
    }
}

export const useFormState = <T extends Record<string, unknown>>(initialState: T | (() => T)) => {
    const initial = typeof initialState === 'function' ? initialState() : initialState
    const [form, dispatch] = useReducer(formReducer<T>, initial)

    const updateField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
        dispatch({ type: 'SET_FIELD', key, value })
    }, [])

    const resetForm = useCallback((newState: T) => {
        dispatch({ type: 'RESET', payload: newState })
    }, [])

    return [form, updateField, resetForm] as const
}
